/**
 * Tusni Theme — Main JavaScript
 * Handles: Header scroll, cart drawer, animations, accordions,
 *          gallery, variant selection, mobile menu, sort/filter
 */

(function () {
  'use strict';

  // =========================================================
  // UTILITIES
  // =========================================================
  const $ = (sel, parent = document) => parent.querySelector(sel);
  const $$ = (sel, parent = document) => [...parent.querySelectorAll(sel)];
  const on = (el, ev, fn) => el && el.addEventListener(ev, fn);

  // =========================================================
  // HEADER — sticky scroll behaviour & height sync
  // =========================================================
  const header = $('#site-header');
  let lastScroll = 0;

  function syncHeaderHeight() {
    const annEl = $('[data-announcement-bar]');
    const headerEl = $('#site-header');
    if (!headerEl) return;
    const annH = annEl ? annEl.offsetHeight : 0;
    const headerH = headerEl.offsetHeight;
    const total = annH + headerH;
    if (total > 0) {
      document.documentElement.style.setProperty('--header-total-height', `${total}px`);
    }
  }
  syncHeaderHeight();
  window.addEventListener('resize', syncHeaderHeight, { passive: true });
  window.addEventListener('load', syncHeaderHeight, { passive: true });

  // =========================================================
  // ANNOUNCEMENT BAR — rotate messages
  // =========================================================
  const announcementBar = $('[data-announcement-bar]');
  if (announcementBar) {
    const items = $$('[data-announcement-item]', announcementBar);
    const prevBtn = $('[data-announcement-prev]', announcementBar);
    const nextBtn = $('[data-announcement-next]', announcementBar);

    if (items.length > 1) {
      let activeIndex = items.findIndex(item => item.classList.contains('is-active'));
      let autoRotateTimer = null;
      const intervalMs = Math.max(2000, parseInt(announcementBar.dataset.interval || '4000', 10));

      if (activeIndex < 0) activeIndex = 0;

      function setActive(index) {
        activeIndex = index;
        items.forEach((item, i) => {
          item.classList.toggle('is-active', i === activeIndex);
        });
      }

      function showNext() {
        setActive((activeIndex + 1) % items.length);
      }

      function showPrev() {
        setActive((activeIndex - 1 + items.length) % items.length);
      }

      function stopAutoRotate() {
        if (autoRotateTimer) {
          clearInterval(autoRotateTimer);
          autoRotateTimer = null;
        }
      }

      function startAutoRotate() {
        if (announcementBar.dataset.rotate !== 'true' || autoRotateTimer) return;
        autoRotateTimer = setInterval(showNext, intervalMs);
      }

      setActive(activeIndex);

      on(prevBtn, 'click', () => {
        showPrev();
        stopAutoRotate();
        startAutoRotate();
      });

      on(nextBtn, 'click', () => {
        showNext();
        stopAutoRotate();
        startAutoRotate();
      });

      on(announcementBar, 'mouseenter', stopAutoRotate);
      on(announcementBar, 'mouseleave', startAutoRotate);
      on(announcementBar, 'focusin', stopAutoRotate);
      on(announcementBar, 'focusout', () => {
        setTimeout(() => {
          if (!announcementBar.contains(document.activeElement)) {
            startAutoRotate();
          }
        }, 0);
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          stopAutoRotate();
        } else {
          startAutoRotate();
        }
      });

      startAutoRotate();
    } else if (items.length === 1) {
      items[0].classList.add('is-active');
    }
  }

  window.addEventListener('scroll', () => {
    const current = window.scrollY;
    if (current > 80) {
      header?.classList.add('scrolled');
    } else {
      header?.classList.remove('scrolled');
    }
    lastScroll = current;
  }, { passive: true });

  // =========================================================
  // MOBILE MENU
  // =========================================================
  const hamburger = $('#hamburger-btn');
  const mobileNav = $('#mobile-nav');
  const overlay = $('#site-overlay');
  const mobileMenuToggles = mobileNav ? $$('[data-mobile-nav-toggle]', mobileNav) : [];

  function collapseMobileSubmenus() {
    mobileMenuToggles.forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.closest('.mobile-nav__item')?.classList.remove('is-expanded');
    });
  }

  function closeMobileMenu() {
    mobileNav?.classList.remove('open');
    hamburger?.classList.remove('is-open');
    hamburger?.setAttribute('aria-expanded', 'false');
    hamburger?.setAttribute('aria-label', 'Open menu');
    collapseMobileSubmenus();

    if (!cartDrawer?.classList.contains('open')) {
      overlay?.classList.remove('active', 'overlay--menu');
    }
    document.body.classList.remove('mobile-menu-open');
  }

  function openMobileMenu() {
    // Keep cart, search, and menu mutually exclusive to avoid stacked drawers.
    closeCart();
    if (typeof window.closeSearchModal === 'function') {
      window.closeSearchModal();
    }

    mobileNav?.classList.add('open');
    hamburger?.classList.add('is-open');
    hamburger?.setAttribute('aria-expanded', 'true');
    hamburger?.setAttribute('aria-label', 'Close menu');
    overlay?.classList.add('active', 'overlay--menu');
    document.body.classList.add('mobile-menu-open');
  }

  on(hamburger, 'click', () => {
    const open = mobileNav?.classList.contains('open');
    if (open) {
      closeMobileMenu();
    } else {
      openMobileMenu();
    }
  });

  mobileMenuToggles.forEach((toggle) => {
    on(toggle, 'click', () => {
      const item = toggle.closest('.mobile-nav__item');
      const shouldExpand = !item?.classList.contains('is-expanded');

      collapseMobileSubmenus();

      if (shouldExpand) {
        item?.classList.add('is-expanded');
        toggle.setAttribute('aria-expanded', 'true');
      }
    });
  });

  on(mobileNav, 'click', (e) => {
    const link = e.target.closest('.mobile-nav__link, .mobile-nav__submenu-link, .mobile-nav__account, .mobile-nav__social');
    if (link) closeMobileMenu();
  });

  // =========================================================
  // CART DRAWER
  // =========================================================
  const cartDrawer = $('#cart-drawer');
  const cartBtn = $('#cart-btn');
  const cartClose = $('#cart-drawer-close');

  let currentRecProductId = null;
  let isRecLoading = false;

  function openCart() {
    closeMobileMenu();
    if (typeof window.closeSearchModal === 'function') {
      window.closeSearchModal();
    }
    cartDrawer?.classList.add('open');
    overlay?.classList.remove('overlay--menu');
    overlay?.classList.add('active');
    document.body.classList.add('cart-open');
    cartDrawer?.focus();

    // Load recommendations if not already loaded and cart has items
    const sliderEl = $('#rec-carousel-slider');
    if (sliderEl && (!sliderEl.children.length || !currentRecProductId)) {
      const firstItem = cartDrawer?.querySelector('.cart-item');
      const baseId = cartDrawer?.dataset.baseProductId || firstItem?.dataset.productId;
      if (baseId) {
        loadCartRecommendations(baseId);
      }
    }
  }

  function closeCart() {
    cartDrawer?.classList.remove('open');
    if (!mobileNav?.classList.contains('open')) {
      overlay?.classList.remove('active', 'overlay--menu');
    }
    document.body.classList.remove('cart-open');
  }

  on(cartBtn, 'click', openCart);
  on(cartClose, 'click', closeCart);
  on(overlay, 'click', () => {
    closeCart();
    closeMobileMenu();
  });

  // Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeCart();
      closeMobileMenu();
      if (typeof window.closeSearchModal === 'function') {
        window.closeSearchModal();
      }
    }
  });

  // =========================================================
  // MONEY & HTML HELPERS
  // =========================================================
  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    const rupees = Math.round(cents / 100);
    return '₹' + rupees.toLocaleString('en-IN');
  }

  function formatRecPrice(val) {
    if (val === null || val === undefined) return '';
    let rupees = 0;
    if (typeof val === 'string') {
      if (val.includes('.')) {
        rupees = Math.round(parseFloat(val));
      } else {
        const n = parseInt(val, 10) || 0;
        rupees = Math.round(n > 100000 ? n / 100 : n);
      }
    } else if (typeof val === 'number') {
      rupees = Math.round(val / 100);
    }
    return 'MRP ₹' + rupees.toLocaleString('en-IN');
  }

  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }


  // =========================================================
  // CART DRAWER RECOMMENDATIONS (YOU MAY ALSO LIKE)
  // =========================================================
  async function loadCartRecommendations(productId) {
    if (!productId || isRecLoading) return;
    const recSection = $('#cart-drawer-recommendations');
    const sliderEl = $('#rec-carousel-slider');
    const dotsEl = $('#rec-carousel-dots');
    if (!recSection || !sliderEl || !dotsEl) return;

    if (currentRecProductId === String(productId) && sliderEl.children.length > 0) {
      recSection.style.display = 'block';
      return;
    }

    isRecLoading = true;
    currentRecProductId = String(productId);

    try {
      let recProducts = [];

      // 1. Fetch official Shopify Product Recommendations
      try {
        const res = await fetch(`/recommendations/products.json?product_id=${productId}&limit=6&intent=related`);
        if (res.ok) {
          const data = await res.json();
          if (data && Array.isArray(data.products) && data.products.length > 0) {
            recProducts = data.products;
          }
        }
      } catch (err) {
        console.warn('Shopify recommendations fetch error:', err);
      }

      // 2. Fallback to collection products if recommendations are empty
      if (recProducts.length < 2) {
        try {
          const fbRes = await fetch('/collections/all/products.json?limit=10');
          if (fbRes.ok) {
            const fbData = await fbRes.json();
            if (fbData && Array.isArray(fbData.products)) {
              const currentCartIds = [...document.querySelectorAll('.cart-item')].map(el => String(el.dataset.productId));
              const fallbackItems = fbData.products.filter(p => !currentCartIds.includes(String(p.id)) && String(p.id) !== String(productId));
              recProducts = [...recProducts, ...fallbackItems].slice(0, 6);
            }
          }
        } catch (err) {
          console.warn('Fallback products fetch error:', err);
        }
      }

      if (!recProducts.length) {
        recSection.style.display = 'none';
        isRecLoading = false;
        return;
      }

      // Normalize recommendations data
      const normalized = recProducts.map(p => {
        const firstVariant = (p.variants && p.variants.find(v => v.available)) || (p.variants && p.variants[0]) || {};
        let imgSrc = '';
        if (typeof p.featured_image === 'string') {
          imgSrc = p.featured_image;
        } else if (p.featured_image && p.featured_image.src) {
          imgSrc = p.featured_image.src;
        } else if (p.images && p.images.length > 0) {
          imgSrc = typeof p.images[0] === 'string' ? p.images[0] : (p.images[0].src || '');
        }

        const rawPrice = firstVariant.price !== undefined ? firstVariant.price : p.price;
        return {
          id: p.id,
          variantId: firstVariant.id,
          title: p.title || '',
          url: p.url || (p.handle ? `/products/${p.handle}` : '#'),
          image: imgSrc,
          priceFormatted: formatRecPrice(rawPrice)
        };
      }).filter(p => p.variantId);

      if (!normalized.length) {
        recSection.style.display = 'none';
        isRecLoading = false;
        return;
      }

      // Render recommendation cards into slider
      sliderEl.innerHTML = normalized.map((p, idx) => `
        <div class="cart-rec-card" data-index="${idx}">
          <a href="${p.url}" class="cart-rec-card__image">
            ${p.image ? `<img src="${p.image}" alt="${escapeHtml(p.title)}" loading="lazy" width="160">` : ''}
          </a>
          <div class="cart-rec-card__info">
            <a href="${p.url}" class="cart-rec-card__title" title="${escapeHtml(p.title)}">${escapeHtml(p.title)}</a>
            <p class="cart-rec-card__price">${p.priceFormatted}</p>
            <button type="button" class="cart-rec-card__add-btn" data-rec-variant-id="${p.variantId}">
              Add to cart
            </button>
          </div>
        </div>
      `).join('');

      // Render pagination dots
      dotsEl.innerHTML = normalized.map((_, idx) => `
        <button type="button" class="cart-rec-dot ${idx === 0 ? 'active' : ''}" data-index="${idx}" aria-label="Slide ${idx + 1}"></button>
      `).join('');

      recSection.style.display = 'block';

      // Setup dots & slider sync
      setupRecSliderEvents(sliderEl, dotsEl);

    } catch (err) {
      console.error('Error loading recommendations:', err);
      recSection.style.display = 'none';
    } finally {
      isRecLoading = false;
    }
  }

  function setupRecSliderEvents(sliderEl, dotsEl) {
    if (!sliderEl || !dotsEl) return;

    // Dot click
    dotsEl.onclick = (e) => {
      const dot = e.target.closest('.cart-rec-dot');
      if (!dot) return;
      const idx = parseInt(dot.dataset.index, 10);
      const cards = sliderEl.querySelectorAll('.cart-rec-card');
      if (cards[idx]) {
        cards[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    };

    // Scroll sync
    let scrollDebounce;
    sliderEl.onscroll = () => {
      clearTimeout(scrollDebounce);
      scrollDebounce = setTimeout(() => {
        const cards = [...sliderEl.querySelectorAll('.cart-rec-card')];
        if (!cards.length) return;
        const sliderLeft = sliderEl.getBoundingClientRect().left;
        let closestIdx = 0;
        let minDiff = Infinity;
        cards.forEach((card, i) => {
          const diff = Math.abs(card.getBoundingClientRect().left - sliderLeft);
          if (diff < minDiff) {
            minDiff = diff;
            closestIdx = i;
          }
        });
        const allDots = dotsEl.querySelectorAll('.cart-rec-dot');
        allDots.forEach((d, i) => {
          d.classList.toggle('active', i === closestIdx);
        });
      }, 40);
    };
  }

  // Add recommended product to cart via AJAX
  async function addRecToCart(variantId, btn) {
    if (!variantId || btn.disabled) return;
    const originalText = btn.textContent;
    btn.textContent = 'Adding...';
    btn.disabled = true;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });

      if (res.ok) {
        btn.textContent = 'Added ✓';
        const addedItem = await res.json().catch(() => ({}));
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        updateCartUI(cart, addedItem?.product_id);

        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 1500);
      } else {
        btn.textContent = 'Failed';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 1500);
      }
    } catch (err) {
      console.error('Failed to add recommendation to cart:', err);
      btn.textContent = originalText;
      btn.disabled = false;
    }
  }

  // =========================================================
  // CART DRAWER INTERACTIONS (QUANTITY, REMOVE, SAVE, REC)
  // =========================================================
  document.addEventListener('click', async (e) => {
    // 1. Recommendation "Add to cart"
    const recAddBtn = e.target.closest('[data-rec-variant-id]');
    if (recAddBtn) {
      e.preventDefault();
      const variantId = recAddBtn.dataset.recVariantId;
      await addRecToCart(variantId, recAddBtn);
      return;
    }


    // 3. Continue shopping close
    const contBtn = e.target.closest('#cart-drawer-continue');
    if (contBtn && window.location.pathname.indexOf('/cart') === -1) {
      e.preventDefault();
      closeCart();
      return;
    }

    // 4. Cart item quantity & remove
    const btn = e.target.closest('[data-action]');
    if (!btn) return;

    const action = btn.dataset.action;
    const key = btn.dataset.key;
    if (!key) return;

    e.preventDefault();

    if (action === 'remove') {
      await updateCartItem(key, 0);
    } else if (action === 'increase' || action === 'decrease') {
      const qtyEl = btn.closest('.cart-item')?.querySelector('.cart-item__qty-num');
      const current = parseInt(qtyEl?.textContent || '1', 10);
      const newQty = action === 'increase' ? current + 1 : Math.max(0, current - 1);
      await updateCartItem(key, newQty);
    }
  });

  async function updateCartItem(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: key, quantity })
      });
      if (res.ok) {
        const cart = await res.json();
        updateCartUI(cart);
      }
    } catch (err) {
      console.error('Cart update failed:', err);
    }
  }

  // =========================================================
  // DYNAMIC CART DRAWER UI UPDATE
  // =========================================================
  function updateCartUI(cart, addedProductId) {
    if (!cart) return;

    // 1. Update header cart count badge
    const countEl = $('#cart-count');
    if (countEl) {
      countEl.textContent = cart.item_count > 0 ? cart.item_count : '';
      countEl.dataset.count = cart.item_count;
    }

    // 2. Update cart drawer count header (if exists)
    const drawerCountEl = $('#cart-drawer-count');
    if (drawerCountEl) {
      const itemWord = cart.item_count === 1 ? 'item' : 'items';
      drawerCountEl.textContent = `${cart.item_count} ${itemWord}`;
    }

    // 3. Update cart drawer items body
    const drawerItems = $('#cart-drawer-items') || $('#cart-drawer-body');
    const recSection = $('#cart-drawer-recommendations');

    if (drawerItems) {
      if (cart.item_count === 0 || !cart.items || cart.items.length === 0) {
        drawerItems.innerHTML = `
          <div class="cart-drawer__empty">
            <svg class="cart-drawer__empty-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 01-8 0"/>
            </svg>
            <p class="cart-drawer__empty-text">Your cart is empty</p>
            <p class="cart-drawer__empty-sub">Add pieces to begin your order.</p>
          </div>
        `;
        if (recSection) recSection.style.display = 'none';
        currentRecProductId = null;
      } else {
        drawerItems.innerHTML = cart.items.map(item => {
          const imgSrc = item.image || (item.featured_image ? item.featured_image.url : '');
          const imgHtml = imgSrc
            ? `<img src="${imgSrc}" alt="${escapeHtml(item.product_title || item.title)}" loading="lazy" width="220">`
            : '';
          const variantTitle = (item.variant_title && item.variant_title !== 'Default Title') ? item.variant_title : '';
          const variantHtml = variantTitle
            ? `<span class="cart-item__variant">${escapeHtml(variantTitle)}</span>`
            : '';

          return `
            <div class="cart-item" data-item-key="${item.key}" data-product-id="${item.product_id}">
              <a href="${item.url}" class="cart-item__image">
                ${imgHtml}
              </a>
              <div class="cart-item__info">
                <a href="${item.url}" class="cart-item__title">${escapeHtml(item.product_title || item.title)}</a>
                <p class="cart-item__price">${formatMoney(item.final_line_price)}</p>
                ${variantHtml}
                <div class="cart-item__actions-row">
                  <div class="cart-item__qty-box">
                    <button class="cart-item__qty-btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease quantity">−</button>
                    <span class="cart-item__qty-num">${item.quantity}</span>
                    <button class="cart-item__qty-btn" data-action="increase" data-key="${item.key}" aria-label="Increase quantity">+</button>
                  </div>
                  <button class="cart-item__remove" data-action="remove" data-key="${item.key}">Remove</button>
                </div>
              </div>
            </div>
          `;
        }).join('');

        // Show recommendations section and load recommendations based on product
        if (recSection) recSection.style.display = 'block';
        const targetProdId = addedProductId || (cart.items && cart.items[0] && cart.items[0].product_id);
        if (targetProdId) {
          loadCartRecommendations(targetProdId);
        }
      }
    }

    // 4. Update cart drawer footer
    const drawerFooter = $('#cart-drawer-footer');
    if (drawerFooter) {
      if (cart.item_count > 0) {
        drawerFooter.innerHTML = `
          <div class="cart-subtotal">
            <span class="cart-subtotal__label">Subtotal</span>
            <span class="cart-subtotal__amount" id="cart-subtotal">${formatMoney(cart.total_price)}</span>
          </div>
          <a href="/checkout" class="cart-drawer__checkout" id="cart-drawer-checkout">
            Checkout — ${formatMoney(cart.total_price)}
          </a>
          <a href="/collections/all" class="cart-drawer__continue" id="cart-drawer-continue">Continue shopping</a>
        `;
      } else {
        drawerFooter.innerHTML = `
          <a href="/collections/all" class="cart-drawer__continue" id="cart-drawer-continue">Continue shopping</a>
        `;
      }
    }
  }

  // Expose helpers globally
  window.openCart = openCart;
  window.closeCart = closeCart;
  window.updateCartUI = updateCartUI;
  window.loadCartRecommendations = loadCartRecommendations;

  // Preload recommendations if cart already has items on initial load
  const initialBaseId = cartDrawer?.dataset.baseProductId;
  if (initialBaseId) {
    loadCartRecommendations(initialBaseId);
  }

  // =========================================================
  // PRODUCT PAGE QUANTITY SELECTOR
  // =========================================================
  document.addEventListener('click', (e) => {
    const decBtn = e.target.closest('[data-quantity-decrease]');
    const incBtn = e.target.closest('[data-quantity-increase]');
    if (!decBtn && !incBtn) return;

    e.preventDefault();
    const wrapper = (decBtn || incBtn).closest('.product-quantity');
    if (!wrapper) return;
    const input = wrapper.querySelector('input[name="quantity"]');
    if (!input) return;

    let val = parseInt(input.value, 10);
    if (isNaN(val) || val < 1) val = 1;

    if (decBtn) {
      if (val > 1) {
        input.value = val - 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else if (incBtn) {
      input.value = val + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  document.addEventListener('change', (e) => {
    if (e.target.matches('.product-quantity input[name="quantity"]')) {
      let val = parseInt(e.target.value, 10);
      if (isNaN(val) || val < 1) {
        e.target.value = 1;
      }
    }
  });

  // =========================================================
  // ADD TO CART & BUY IT NOW SUBMISSION
  // =========================================================
  let lastClickedSubmitBtn = null;
  document.addEventListener('click', (e) => {
    const addBtn = e.target.closest('#add-to-cart-btn');
    if (addBtn) lastClickedSubmitBtn = 'add';
    const buyNowBtn = e.target.closest('#buy-now-btn');
    if (buyNowBtn) lastClickedSubmitBtn = 'buy_now';
  });

  document.addEventListener('submit', async (e) => {
    const pForm = e.target.closest('#product-form');
    if (!pForm) return;
    e.preventDefault();

    const addBtn = pForm.querySelector('#add-to-cart-btn') || $('#add-to-cart-btn');
    const buyNowBtn = pForm.querySelector('#buy-now-btn') || $('#buy-now-btn');

    const isBuyNow = (e.submitter && (e.submitter.id === 'buy-now-btn' || e.submitter.name === 'buy_now' || e.submitter.name === 'checkout')) ||
                     lastClickedSubmitBtn === 'buy_now' ||
                     lastClickedSubmitBtn === 'checkout';
    lastClickedSubmitBtn = null;

    const activeBtn = isBuyNow ? buyNowBtn : addBtn;
    const originalText = activeBtn ? activeBtn.textContent : '';

    const idInput = pForm.querySelector('#variant-id') || $('#variant-id');
    if (!idInput || !idInput.value) {
      if (activeBtn) activeBtn.textContent = 'Please select options';
      setTimeout(() => { if (activeBtn) activeBtn.textContent = originalText; }, 2000);
      return;
    }

    if (isBuyNow) {
      if (buyNowBtn) {
        buyNowBtn.textContent = 'Processing...';
        buyNowBtn.disabled = true;
      }
    } else {
      if (addBtn) {
        addBtn.textContent = addBtn.dataset.addingLabel || 'Adding...';
        addBtn.disabled = true;
      }
    }

    try {
      const formData = new FormData(pForm);
      const qtyInput = pForm.querySelector('input[name="quantity"]');
      const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      formData.set('id', idInput.value);
      formData.set('quantity', qty);

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' }
      });

      if (res.ok) {
        if (isBuyNow) {
          // Fetch fresh cart and update cart drawer so the item is in the cart
          const addedItem = await res.json().catch(() => ({}));
          const addedProductId = addedItem ? addedItem.product_id : null;
          try {
            const cartRes = await fetch('/cart.js');
            const cart = await cartRes.json();
            updateCartUI(cart, addedProductId);
          } catch (e) {
            console.warn('Cart refresh failed:', e);
          }

          // Restore Buy Now button label and enable it
          if (buyNowBtn) {
            buyNowBtn.textContent = originalText || buyNowBtn.dataset.defaultLabel || 'Buy It Now';
            buyNowBtn.disabled = false;
          }

          // Open Razorpay Magic Checkout with the selected product
          triggerRazorpayCheckout();
          return;
        }

        if (!cartDrawer) {
          window.location.href = '/cart';
          return;
        }

        if (addBtn) addBtn.textContent = addBtn.dataset.addedLabel || 'Added ✓';

        const addedItem = await res.json().catch(() => ({}));
        const addedProductId = addedItem ? addedItem.product_id : null;

        // Fetch fresh cart JSON and update drawer
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        updateCartUI(cart, addedProductId);

        openCart(); // Open cart immediately without artificial delay
      } else {
        const errData = await res.json().catch(() => ({}));
        const errMsg = errData.description || 'Try again';
        if (activeBtn) activeBtn.textContent = errMsg;
      }
    } catch (err) {
      console.error('Add to cart failed:', err);
      if (activeBtn) activeBtn.textContent = 'Error';
    }

    setTimeout(() => {
      if (activeBtn) {
        activeBtn.textContent = originalText;
        activeBtn.disabled = false;
      }
    }, 2000);
  });

  // =========================================================
  // RAZORPAY MAGIC CHECKOUT TRIGGER
  // =========================================================
  function triggerRazorpayCheckout() {
    // 1. Direct call if Razorpay Magic Checkout API is exposed on window
    if (typeof window.openRzpCheckout === 'function') {
      try {
        window.openRzpCheckout(null, { pageType: 'product', isBuyNow: true });
        return;
      } catch (err) {
        console.warn('window.openRzpCheckout failed, trying element trigger:', err);
      }
    }

    // 2. Click the cart drawer checkout anchor (which Razorpay's click listener captures)
    const drawerCheckout = document.getElementById('cart-drawer-checkout') || document.querySelector('a.cart-drawer__checkout');
    if (drawerCheckout) {
      drawerCheckout.click();
      return;
    }

    // 3. Create a temporary checkout anchor and dispatch click to trigger Razorpay capture
    const tempCheckoutLink = document.createElement('a');
    tempCheckoutLink.href = '/checkout';
    tempCheckoutLink.style.display = 'none';
    document.body.appendChild(tempCheckoutLink);
    tempCheckoutLink.click();
    setTimeout(() => {
      tempCheckoutLink.remove();
    }, 500);
  }

  // =========================================================
  // BFCache (BACK / FORWARD CACHE) BUTTON RESET
  // =========================================================
  function resetActionButtons() {
    const buyNowBtn = document.getElementById('buy-now-btn');
    if (buyNowBtn) {
      const defaultText = buyNowBtn.dataset.defaultLabel || 'Buy It Now';
      buyNowBtn.textContent = defaultText;
      const pForm = buyNowBtn.closest('#product-form') || document.getElementById('product-form');
      const addBtn = pForm ? pForm.querySelector('#add-to-cart-btn') : null;
      if (addBtn && addBtn.disabled && (addBtn.textContent || '').toLowerCase().includes('sold out')) {
        buyNowBtn.disabled = true;
      } else {
        buyNowBtn.disabled = false;
      }
    }

    const addBtn = document.getElementById('add-to-cart-btn');
    if (addBtn && addBtn.dataset.addLabel && !addBtn.disabled) {
      addBtn.textContent = addBtn.dataset.addLabel;
    }
  }

  window.addEventListener('pageshow', () => {
    resetActionButtons();
  });

  window.addEventListener('pagehide', () => {
    resetActionButtons();
  });

  // =========================================================
  // VARIANT SELECTOR
  // =========================================================
  function initProductVariantSelector() {
    const variantsJson = $('#product-variants-json');
    if (!variantsJson) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsJson.textContent);
    } catch (err) {
      console.error('Failed to parse product variants JSON:', err);
      return;
    }

    const selectors = $$('.size-selector[data-option-index]');

    // Ensure every selector group has an active button on load
    selectors.forEach(group => {
      if (!group.querySelector('.size-btn.active')) {
        const firstBtn = group.querySelector('.size-btn');
        if (firstBtn) firstBtn.classList.add('active');
      }
    });

    function getCurrentOptions() {
      const currentSelectors = $$('.size-selector[data-option-index]');
      const sortedSelectors = [...currentSelectors].sort((a, b) => {
        return parseInt(a.dataset.optionIndex, 10) - parseInt(b.dataset.optionIndex, 10);
      });

      return sortedSelectors.map(group => {
        const activeBtn = group.querySelector('.size-btn.active');
        return activeBtn ? activeBtn.dataset.value.trim() : null;
      });
    }

    function handleVariantChange() {
      const selectedOptions = getCurrentOptions();
      if (selectedOptions.includes(null)) return;

      const variant = variants.find(v => {
        return selectedOptions.every((optVal, idx) => {
          if (Array.isArray(v.options) && v.options[idx] !== undefined) {
            return String(v.options[idx]).trim() === optVal;
          }
          const prop = `option${idx + 1}`;
          return String(v[prop]).trim() === optVal;
        });
      });

      const idInput = $('#variant-id');
      const priceEl = $('#product-price');
      const compareEl = $('#product-compare-price') || $('.product-info__compare');
      const addBtn = $('#add-to-cart-btn');
      const buyNowBtn = $('#buy-now-btn');
      const skuEl = $('#product-sku');

      if (variant) {
        if (idInput) idInput.value = variant.id;
        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        if (skuEl) skuEl.textContent = variant.sku || '—';

        // Update Compare At Price
        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = formatMoney(variant.compare_at_price);
            compareEl.style.display = '';
          } else {
            compareEl.style.display = 'none';
          }
        }

        // Availability and Buttons
        if (addBtn) {
          if (variant.available) {
            addBtn.disabled = false;
            addBtn.textContent = addBtn.dataset.addLabel || 'Add to Cart';
          } else {
            addBtn.disabled = true;
            addBtn.textContent = addBtn.dataset.soldoutLabel || 'Sold Out';
          }
        }

        if (buyNowBtn) {
          buyNowBtn.disabled = !variant.available;
        }

        // Update URL param without full page reload
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);

        // Notify other listeners (e.g. custom size chart)
        document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: variant } }));

        // Update image if variant has featured image
        if (variant.featured_image && variant.featured_image.src) {
          const rawSrc = variant.featured_image.src;
          const imgFilename = rawSrc.split('/').pop().split('?')[0];
          const thumbs = $$('.product-gallery__thumb');
          let matchedThumb = null;
          thumbs.forEach(thumb => {
            const thumbSrc = thumb.dataset.src || '';
            if (thumbSrc.includes(imgFilename)) {
              matchedThumb = thumb;
            }
          });
          if (matchedThumb) {
            thumbs.forEach(t => t.classList.remove('active'));
            matchedThumb.classList.add('active');
            scrollToGalleryImage(matchedThumb.dataset.index);
          }
        }
      } else {
        // Combination does not exist
        if (idInput) idInput.value = '';
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = 'Unavailable';
        }
        if (buyNowBtn) {
          buyNowBtn.disabled = true;
        }
      }
    }

    window._currentVariantChangeHandler = handleVariantChange;
    handleVariantChange();
  }

  // Delegated size-btn click so it works for present and future dynamically swapped elements
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.size-btn');
    if (!btn) return;
    const selector = btn.closest('.size-selector');
    if (!selector) return;

    e.preventDefault();
    $$('.size-btn', selector).forEach(b => b.classList.remove('active'));
    btn.classList.add('active');

    const optIndex = selector.dataset.optionIndex;
    const selectedLabel = $(`#option-${optIndex}-selected`);
    if (selectedLabel) {
      selectedLabel.textContent = btn.dataset.value;
    }

    if (typeof window._currentVariantChangeHandler === 'function') {
      window._currentVariantChangeHandler();
    }
  });

  window.initProductVariantSelector = initProductVariantSelector;
  initProductVariantSelector();

  // =========================================================
  // PRODUCT GALLERY — SYNCHRONIZED SCROLL & SCROLLSPY
  // =========================================================
  function getHeaderOffsetHeight() {
    const rawVal = getComputedStyle(document.documentElement).getPropertyValue('--header-total-height');
    const parsed = parseInt(rawVal, 10);
    if (!isNaN(parsed) && parsed > 0) return parsed;
    const header = document.querySelector('.header') || document.querySelector('header');
    return header ? header.offsetHeight : 70;
  }

  let isManualGalleryScroll = false;
  let manualGalleryScrollTimeout = null;

  function scrollToGalleryImage(index) {
    const targetItem = document.getElementById(`gallery-image-${index}`);
    const mainList = document.getElementById('gallery-main-list');
    if (!targetItem || !mainList) return;

    isManualGalleryScroll = true;
    clearTimeout(manualGalleryScrollTimeout);

    const isMobile = window.innerWidth <= 900;

    if (isMobile) {
      mainList.scrollTo({
        left: targetItem.offsetLeft,
        behavior: 'smooth'
      });
    } else {
      mainList.scrollTo({
        top: targetItem.offsetTop,
        behavior: 'smooth'
      });
    }

    manualGalleryScrollTimeout = setTimeout(() => {
      isManualGalleryScroll = false;
    }, 600);
  }

  function setActiveGalleryThumb(index, scrollThumb = true) {
    const thumbs = $$('.product-gallery__thumb');
    const thumbsContainer = document.getElementById('gallery-thumbs');
    thumbs.forEach(t => {
      if (String(t.dataset.index) === String(index)) {
        t.classList.add('active');
        if (scrollThumb && thumbsContainer) {
          if (window.innerWidth <= 900) {
            const targetLeft = t.offsetLeft - (thumbsContainer.clientWidth - t.clientWidth) / 2;
            thumbsContainer.scrollTo({
              left: Math.max(0, targetLeft),
              behavior: 'smooth'
            });
          } else {
            const targetTop = t.offsetTop - (thumbsContainer.clientHeight - t.clientHeight) / 2;
            thumbsContainer.scrollTo({
              top: Math.max(0, targetTop),
              behavior: 'smooth'
            });
          }
        }
      } else {
        t.classList.remove('active');
      }
    });
  }

  // Thumbnail Click
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('.product-gallery__thumb');
    if (!thumb) return;

    e.preventDefault();
    const index = thumb.dataset.index;
    if (index === undefined) return;

    setActiveGalleryThumb(index, true);
    scrollToGalleryImage(index);
  });

  // Gallery scroll sync (both mobile swipe and desktop overflow-y)
  function initGalleryScrollListeners() {
    const galleryMainList = document.getElementById('gallery-main-list');
    if (galleryMainList && !galleryMainList.dataset.scrollBound) {
      galleryMainList.dataset.scrollBound = 'true';
      let galleryScrollTimeout = null;
      galleryMainList.addEventListener('scroll', () => {
        if (isManualGalleryScroll) return;
        clearTimeout(galleryScrollTimeout);
        galleryScrollTimeout = setTimeout(() => {
          const isMobile = window.innerWidth <= 900;
          const mainItems = $$('.product-gallery__main-item');
          if (!mainItems.length) return;

          if (isMobile) {
            const scrollLeft = galleryMainList.scrollLeft;
            const itemWidth = galleryMainList.clientWidth || 1;
            const newIndex = Math.round(scrollLeft / itemWidth);
            const clampedIndex = Math.max(0, Math.min(newIndex, mainItems.length - 1));
            setActiveGalleryThumb(clampedIndex, true);
          } else {
            const scrollTop = galleryMainList.scrollTop;
            let activeIndex = 0;
            mainItems.forEach((item, idx) => {
              if (item.offsetTop <= scrollTop + 60) {
                activeIndex = idx;
              }
            });
            setActiveGalleryThumb(activeIndex, true);
          }
        }, 50);
      }, { passive: true });

      // Allow mousewheel on thumbs container to scroll main list on desktop
      const thumbsContainer = document.getElementById('gallery-thumbs');
      if (thumbsContainer && !thumbsContainer.dataset.wheelBound) {
        thumbsContainer.dataset.wheelBound = 'true';
        thumbsContainer.addEventListener('wheel', (e) => {
          if (window.innerWidth > 900) {
            galleryMainList.scrollTop += e.deltaY;
          }
        }, { passive: true });
      }
    }
  }

  window.initGalleryScrollListeners = initGalleryScrollListeners;
  initGalleryScrollListeners();

  function updateSyncAndSpy() {
    const gallery = document.querySelector('.product-gallery');
    const mainItems = $$('.product-gallery__main-item');
    const thumbs = $$('.product-gallery__thumb');
    const infoInner = document.getElementById('product-info-inner');
    const thumbsInner = document.getElementById('gallery-thumbs-inner');

    if (infoInner && infoInner.style.transform) infoInner.style.transform = '';
    if (thumbsInner && thumbsInner.style.transform) thumbsInner.style.transform = '';

    if (!gallery || window.innerWidth <= 900) return;

    const headerOffset = getHeaderOffsetHeight();

    // Scrollspy calculation for vertically scrolling images on desktop
    if (mainItems.length && thumbs.length && !isManualGalleryScroll) {
      const probePoint = headerOffset + Math.min(window.innerHeight * 0.35, 260);
      let activeIndex = 0;
      let minDistance = Infinity;

      mainItems.forEach((item, idx) => {
        const rect = item.getBoundingClientRect();
        if (rect.top <= probePoint && rect.bottom > probePoint) {
          activeIndex = idx;
          minDistance = 0;
        } else if (minDistance !== 0) {
          const dist = Math.abs(rect.top - probePoint);
          if (dist < minDistance) {
            minDistance = dist;
            activeIndex = idx;
          }
        }
      });

      setActiveGalleryThumb(activeIndex, false);
    }
  }

  function initGallerySyncScroll() {
    let ticking = false;

    const requestTick = () => {
      // On mobile (<= 900px), do NOT run desktop sync/spy on scroll at all
      if (window.innerWidth <= 900) return;
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateSyncAndSpy();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', () => {
      const infoInner = document.getElementById('product-info-inner');
      const thumbsInner = document.getElementById('gallery-thumbs-inner');
      if (window.innerWidth <= 900) {
        if (infoInner) infoInner.style.transform = '';
        if (thumbsInner) thumbsInner.style.transform = '';
      } else {
        requestTick();
      }
    }, { passive: true });

    const infoInner = document.getElementById('product-info-inner');
    const gallery = document.querySelector('.product-gallery');
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => {
        if (window.innerWidth > 900) {
          requestTick();
        }
      });
      if (infoInner) ro.observe(infoInner);
      if (gallery) ro.observe(gallery);
    }

    if (window.innerWidth > 900) {
      requestTick();
    } else {
      if (infoInner) infoInner.style.transform = '';
      const thumbsInner = document.getElementById('gallery-thumbs-inner');
      if (thumbsInner) thumbsInner.style.transform = '';
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGallerySyncScroll);
  } else {
    initGallerySyncScroll();
  }

  // =========================================================
  // ACCORDIONS
  // =========================================================
  function setAccordionHeight(item) {
    const body = item.querySelector('.accordion__body');
    if (!body) return;
    // Measured height instead of a fixed cap so long content never gets clipped.
    body.style.maxHeight = item.classList.contains('open') ? body.scrollHeight + 'px' : '0px';
  }

  $$('.accordion__item.open').forEach(setAccordionHeight);

  window.refreshOpenAccordions = function () {
    $$('.accordion__item.open').forEach(setAccordionHeight);
  };
  window.addEventListener('resize', window.refreshOpenAccordions, { passive: true });
  window.addEventListener('load', window.refreshOpenAccordions, { once: true });

  // Delegated accordion click
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-accordion]');
    if (!btn) return;
    const item = btn.closest('.accordion__item');
    if (!item) return;

    const isOpen = item.classList.contains('open');

    // Close all accordions in same parent
    const siblings = $$('.accordion__item', item.parentElement);
    siblings.forEach(s => {
      s.classList.remove('open');
      setAccordionHeight(s);
    });

    if (!isOpen) item.classList.add('open');
    setAccordionHeight(item);
    setTimeout(updateSyncAndSpy, 320);
  });

  // =========================================================
  // SCROLL ANIMATIONS
  // =========================================================
  let scrollObserver = null;
  if ('IntersectionObserver' in window) {
    scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          scrollObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    $$('[data-animate]').forEach(el => scrollObserver.observe(el));
  } else {
    // Fallback: show everything
    $$('[data-animate]').forEach(el => el.classList.add('is-visible'));
  }

  window.observeAnimateElements = function (container = document) {
    const elements = container.querySelectorAll ? [...container.querySelectorAll('[data-animate]')] : [];
    if (container.matches && container.matches('[data-animate]')) {
      elements.unshift(container);
    }
    if (scrollObserver) {
      elements.forEach(el => {
        if (!el.classList.contains('is-visible')) {
          scrollObserver.observe(el);
        }
      });
    } else {
      elements.forEach(el => el.classList.add('is-visible'));
    }
  };

  // =========================================================
  // AUTOPLAY VIDEOS
  // =========================================================
  function tryPlayVideo(video) {
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;

    const playAttempt = video.play();
    if (playAttempt && typeof playAttempt.catch === 'function') {
      playAttempt.catch(() => {
        // Ignore autoplay rejections; the first user interaction can resume playback.
      });
    }
  }

  const autoplayVideos = $$('[data-autoplay-video]');
  autoplayVideos.forEach((video) => {
    on(video, 'loadeddata', () => tryPlayVideo(video));
    on(video, 'canplay', () => tryPlayVideo(video));
    tryPlayVideo(video);
  });

  if ('IntersectionObserver' in window) {
    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          tryPlayVideo(video);
        } else if (!video.paused) {
          video.pause();
        }
      });
    }, { threshold: 0.35 });

    autoplayVideos.forEach((video) => videoObserver.observe(video));
  }

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      autoplayVideos.forEach((video) => tryPlayVideo(video));
    }
  });

  // =========================================================
  // COLLECTION FILTERS — mobile toggle
  // =========================================================
  const filterToggle = $('#filter-toggle');
  const filterSidebar = $('#filter-sidebar');
  if (filterToggle && filterSidebar) {
    on(filterToggle, 'click', () => {
      const open = filterSidebar.classList.toggle('open');
      filterToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  // =========================================================
  // COLLECTION SORT
  // =========================================================
  const sortSelect = $('#SortBy');
  on(sortSelect, 'change', function () {
    const url = new URL(window.location);
    url.searchParams.set('sort_by', this.value);
    window.location.href = url.toString();
  });

  // =========================================================
  // GRID TOGGLE
  // =========================================================
  $$('.grid-toggle__btn').forEach(btn => {
    on(btn, 'click', function () {
      const cols = this.dataset.cols;
      const grid = $('#products-grid');
      if (grid) {
        grid.dataset.cols = cols;
        grid.style.setProperty('--col-count', cols);
      }
      $$('.grid-toggle__btn').forEach(b => b.classList.remove('active'));
      this.classList.add('active');
    });
  });

  // =========================================================
  // NEWSLETTER FORM SUCCESS
  // =========================================================
  const nlForm = document.querySelector('.newsletter__form form, #ContactFooter');
  if (nlForm) {
    // Shopify handles form submission. Check for success param.
    if (window.location.search.includes('customer_posted=true')) {
      const success = $('#newsletter-success');
      if (success) success.classList.add('show');
      if (nlForm) nlForm.style.display = 'none';
    }
  }

  // =========================================================
  // SMOOTH SCROLL TO ANCHOR
  // =========================================================
  $$('a[href^="#"]').forEach(link => {
    on(link, 'click', function (e) {
      const id = this.hash ? this.hash.substring(1) : '';
      if (!id) {
        // Placeholder "#" link — do nothing instead of jumping to the top.
        e.preventDefault();
        return;
      }
      const target = document.getElementById(id);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  // =========================================================
  // PAGE VISIBILITY (Immediate, zero-delay rendering)
  // =========================================================
  document.body.style.opacity = '1';
  window.addEventListener('pageshow', () => {
    document.body.style.opacity = '1';
  });

})();
